import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { AppProvider } from "@/context/AppContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import SalesDashboard from "@/pages/SalesDashboard";
import Production from "@/pages/Production";
import Feed from "@/pages/Feed";
import Medicine from "@/pages/Medicine";
import Shipment from "@/pages/Shipment";
import Reports from "@/pages/Reports";
import Forecast from "@/pages/Forecast";
import Transfer from "@/pages/Transfer";
import YieldLoss from "@/pages/YieldLoss";
import ResetPassword from "@/pages/ResetPassword";
import Media from "@/pages/Media";
import ModuleSelect from "@/pages/ModuleSelect";
import KumesSelect from "@/pages/KumesSelect";
import Depot from "@/pages/Depot";
import Sales from "@/pages/Sales";
import Accounting from "@/pages/Accounting";
import SettingsHub from "@/pages/SettingsHub";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useEffect } from "react";

// Rota koruması: yetkisiz modül adresine girilirse Modüller ekranına döndür
function Guard({ mod, children }) {
  const { hasModule } = useAuth();
  const ok = hasModule(mod);
  useEffect(() => {
    if (!ok) toast.error("Bu modüle erişim yetkiniz bulunmamaktadır", { id: "no-module-access" });
  }, [ok]);
  return ok ? children : <Navigate to="/" replace />;
}

function AdminOnly({ children }) {
  const { user } = useAuth();
  const ok = user?.role === "admin";
  useEffect(() => {
    if (!ok) toast.error("Bu sayfaya erişim yetkiniz bulunmamaktadır", { id: "no-admin-access" });
  }, [ok]);
  return ok ? children : <Navigate to="/kumes" replace />;
}

function KumesShell() {
  const navigate = useNavigate();
  return <Layout onBackToModules={() => navigate("/kumes-modul")}><Outlet /></Layout>;
}

function Shell() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const back = () => navigate("/");
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-background"><Loader2 className="animate-spin text-primary" size={32} /></div>;
  if (!user) return (
    <Routes>
      <Route path="/sifre-sifirla" element={<ResetPassword />} />
      <Route path="*" element={<Login />} />
    </Routes>
  );
  return (
    <AppProvider>
      <Routes>
        <Route path="/" element={<ModuleSelect />} />
        <Route path="/ayarlar" element={<SettingsHub onBack={back} />} />
        <Route path="/kumes-modul" element={<Guard mod="kumes"><KumesSelect /></Guard>} />
        <Route path="/depo" element={<Guard mod="depot"><Depot onBack={back} /></Guard>} />
        <Route path="/pazarlama" element={<Guard mod="sales"><Sales onBack={back} /></Guard>} />
        <Route path="/on-muhasebe" element={<Guard mod="accounting"><Accounting onBack={back} /></Guard>} />
        <Route element={<Guard mod="kumes"><KumesShell /></Guard>}>
          <Route path="/kumes" element={<Dashboard />} />
          <Route path="/satis-panel" element={<SalesDashboard />} />
          <Route path="/uretim" element={<Production />} />
          <Route path="/yem" element={<Feed />} />
          <Route path="/ilac" element={<Medicine />} />
          <Route path="/sevkiyat" element={<Shipment />} />
          <Route path="/depo-transfer" element={<Transfer />} />
          <Route path="/ongoru" element={<Forecast />} />
          <Route path="/randiman-fire" element={<YieldLoss />} />
          <Route path="/medya" element={<Media />} />
          <Route path="/raporlar" element={<AdminOnly><Reports /></AdminOnly>} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AppProvider>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Shell />
        </BrowserRouter>
        <Toaster position="top-right" richColors offset="calc(env(safe-area-inset-top, 0px) + 16px)" />
      </AuthProvider>
    </div>
  );
}

export default App;
